import React, { useMemo } from 'react';

const TYPE_COLORS = {
  takeout: '#ef4444',
  expansion: '#10b981',
  defense: '#3b82f6',
  custom: '#8b5cf6',
};

const TYPE_LABELS = {
  takeout: 'TKO',
  expansion: 'EXP',
  defense: 'DEF',
  custom: 'CST',
};

function getMonthsBetween(startDate, endDate) {
  const months = [];
  const current = new Date(startDate);
  current.setDate(1);
  const end = new Date(endDate);
  end.setDate(1);

  while (current <= end) {
    months.push({
      key: `${current.getFullYear()}-${current.getMonth()}`,
      label: current.toLocaleDateString('en-US', { month: 'short' }),
      year: current.getFullYear(),
      month: current.getMonth(),
    });
    current.setMonth(current.getMonth() + 1);
  }
  return months;
}

function InitiativeTimeline({ initiatives, onSelect }) {
  // Calculate timeline bounds from all initiatives
  const { months, minDate, maxDate } = useMemo(() => {
    if (!initiatives || initiatives.length === 0) {
      // Default to current month +6 months
      const now = new Date();
      const future = new Date(now);
      future.setMonth(future.getMonth() + 6);
      return {
        months: getMonthsBetween(now, future),
        minDate: now,
        maxDate: future,
      };
    }

    const dates = [];
    for (const init of initiatives) {
      if (init.start_date) dates.push(new Date(init.start_date));
      if (init.target_end_date) dates.push(new Date(init.target_end_date));
      for (const m of (init.milestones || [])) {
        if (m.target_date) dates.push(new Date(m.target_date));
      }
    }

    if (dates.length === 0) {
      const now = new Date();
      const future = new Date(now);
      future.setMonth(future.getMonth() + 6);
      return {
        months: getMonthsBetween(now, future),
        minDate: now,
        maxDate: future,
      };
    }

    // Add 1 month padding on each side
    const min = new Date(Math.min(...dates));
    min.setMonth(min.getMonth() - 1);
    const max = new Date(Math.max(...dates));
    max.setMonth(max.getMonth() + 1);

    return {
      months: getMonthsBetween(min, max),
      minDate: min,
      maxDate: max,
    };
  }, [initiatives]);

  // Calculate position percentages for bars and milestones
  const getPositionPercent = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const totalMs = maxDate.getTime() - minDate.getTime();
    if (totalMs <= 0) return 0;
    const offsetMs = d.getTime() - minDate.getTime();
    return Math.max(0, Math.min(100, (offsetMs / totalMs) * 100));
  };

  // Filter to only show initiatives with dates
  const visibleInitiatives = (initiatives || []).filter(
    init => init.start_date || init.target_end_date
  );

  if (visibleInitiatives.length === 0) {
    return (
      <div className="si-timeline">
        <div className="si-timeline__empty">
          No initiatives with timeline dates. Add start/end dates to see them here.
        </div>
      </div>
    );
  }

  return (
    <div className="si-timeline">
      <div className="si-timeline__header">
        <div className="si-timeline__label-col">Initiative</div>
        <div className="si-timeline__months">
          {months.map((m, i) => (
            <div
              key={m.key}
              className="si-timeline__month"
            >
              {m.label}
              {(i === 0 || m.month === 0) && (
                <span style={{ display: 'block', fontSize: '0.6rem', opacity: 0.7 }}>
                  {m.year}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="si-timeline__body">
        {visibleInitiatives.map(init => {
          const startPct = getPositionPercent(init.start_date);
          const endPct = getPositionPercent(init.target_end_date || init.start_date);
          const barLeft = startPct != null ? startPct : 0;
          const barWidth = endPct != null && startPct != null
            ? Math.max(2, endPct - startPct)
            : 2;
          const color = init.color || TYPE_COLORS[init.initiative_type];

          return (
            <div key={init.id} className="si-timeline__row">
              <div
                className="si-timeline__row-label"
                onClick={() => onSelect?.(init)}
              >
                <span
                  className="si-timeline__row-type"
                  style={{
                    background: `${color}20`,
                    color: color,
                  }}
                >
                  {TYPE_LABELS[init.initiative_type]}
                </span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {init.title}
                </span>
              </div>
              <div className="si-timeline__chart">
                {/* Initiative bar */}
                <div
                  className="si-timeline__bar"
                  style={{
                    left: `${barLeft}%`,
                    width: `${barWidth}%`,
                    background: color,
                  }}
                  title={`${init.title}: ${init.start_date || 'No start'} → ${init.target_end_date || 'No end'}`}
                />
                {/* Milestones */}
                {(init.milestones || []).map((m, i) => {
                  const mPct = getPositionPercent(m.target_date);
                  if (mPct == null) return null;
                  return (
                    <div
                      key={m.id || i}
                      className={`si-timeline__milestone ${m.completed_at ? 'si-timeline__milestone--completed' : ''}`}
                      style={{ left: `${mPct}%` }}
                      title={`${m.title}${m.completed_at ? ' (completed)' : ''}`}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default InitiativeTimeline;
